<template>
  <section>
    <div class="intro-y grid grid-cols-12 gap-3 sm:gap-6 mt-5">
      <div class="col-span-12">
        <h2 class="intro-y text-lg font-medium mr-auto mt-2">Plataformas</h2>
        <p class="text-gray-600 text-xs mt-1">Pasa el mouse sobre una puerta para abrirla · Clic para entrar</p>
      </div>
    </div>

    <div ref="stage" class="hub intro-y mt-5" @pointermove="onMove" @pointerleave="onLeave">
      <div ref="world" class="world">

        <div class="face ceil ceil-bg">
          <div class="lamp lamp--neon flicker" style="top:20%"></div>
          <div class="lamp lamp--neon" style="top:55%"></div>
          <div class="lamp lamp--brand" style="top:85%"></div>
        </div>
        <div class="face floor floor-bg"></div>

        <!-- Paredes con puertas -->
        <div v-for="wall in walls" :key="wall.id" class="face wall-bg" :class="wall.id">
          <button v-for="(door, k) in doorsOn(wall.id)" :key="door.name"
                  type="button" class="door"
                  :style="{ left: slotLeft(wall, k) + 'px' }"
                  :aria-label="'Abrir ' + door.name"
                  :title="door.description"
                  :disabled="busy"
                  @mouseenter="hovered = door.name" @mouseleave="hovered = null"
                  @focus="hovered = door.name" @blur="hovered = null"
                  @click="enter(door)">

            <!-- Letrero -->
            <span class="sign" :class="{ 'sign--active': isActive(door) }">{{ door.name }}</span>

            <!-- Marco -->
            <span class="frame">
              <span class="hole"></span>
              <span class="glow" :style="{ opacity: glowOpacity(door) }"></span>
              <span class="peek-wrap">
                <span class="peek" :style="peekStyle(door)" v-html="icon(door.icon, 40)"></span>
              </span>
              <!-- Hoja de la puerta -->
              <span class="leaf" :style="leafStyle(door)">
                <span class="leaf-icon" :style="iconStyle(door)" v-html="icon(door.icon, 32)"></span>
                <span class="leaf-line"></span>
                <span class="leaf-line"></span>
                <span class="knob"></span>
              </span>
            </span>
          </button>
        </div>

      </div>

      <!-- Transición -->
      <transition name="fade">
        <div v-if="overlay" class="overlay">
          <div class="overlay-icon" v-html="icon(overlay.icon, 48)"></div>
          <h2 class="overlay-title">Abriendo {{ overlay.name }}…</h2>
          <p class="overlay-text">{{ overlay.description }}</p>
          <div class="overlay-bar"><div class="overlay-bar__fill"></div></div>
        </div>
      </transition>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex'
import axios from 'axios'

// wall: "back" | "left" | "right"; action: método de login de la plataforma
const DOORS = [
  { name: 'Shortener',        description: 'Acortador de urls',           wall: 'left',  icon: 'link', action: 'shortener' },
  { name: 'Catalogo Digital', description: 'Piezas HTML, Plataformas',    wall: 'back',  icon: 'book', action: 'catalog' },
  { name: 'Mailjet Report',   description: 'Estadisticas Envios Mailjet', wall: 'back',  icon: 'mail', action: 'mailjet' },
  { name: 'Academy',          description: 'Cursos de Capacitación',      wall: 'back',  icon: 'cap',  action: 'academy' },
  { name: 'Flows',            description: 'Envio de campañas',           wall: 'right', icon: 'flow', action: 'flows' },
]

const ICONS = {
  link: '<path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7"/><path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7"/>',
  book: '<path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20V3H6.5A2.5 2.5 0 0 0 4 5.5z"/><path d="M4 19.5A2.5 2.5 0 0 0 6.5 22H20v-5"/><path d="M9 7h7M9 11h5"/>',
  mail: '<rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 6-10 7L2 6"/>',
  cap:  '<path d="M22 10 12 5 2 10l10 5 10-5z"/><path d="M6 12v5c3 2.5 9 2.5 12 0v-5"/>',
  flow: '<rect x="3" y="3" width="6" height="6" rx="1"/><rect x="15" y="15" width="6" height="6" rx="1"/><path d="M6 9v4a2 2 0 0 0 2 2h7"/><path d="m13 12 3 3-3 3"/>',
}

const ROOM = { W: 900, H: 440, D: 700 }

export default {
  name: 'Platforms',
  data() {
    return {
      loading: true,
      doors: DOORS,
      walls: [
        { id: 'left', width: ROOM.D },
        { id: 'back', width: ROOM.W },
        { id: 'right', width: ROOM.D },
      ],
      hovered: null,
      opened: null,
      overlay: null,
    }
  },
  computed: {
    ...mapGetters({
      user: 'getUser',
    }),
    busy() {
      return !!this.opened
    },
  },
  watch: {
    // Los métodos de login ponen loading en true al terminar la petición
    loading(value) {
      if (value && this.overlay) this.finish()
    },
  },
  mounted() {
    this.cam = { x: 0, y: 0, tx: 0, ty: 0 }
    this.scale = 1
    this.fit()
    window.addEventListener('resize', this.fit)
    const loop = () => {
      const c = this.cam
      c.x += (c.tx - c.x) * 0.06
      c.y += (c.ty - c.y) * 0.06
      this.applyCamera()
      this.raf = requestAnimationFrame(loop)
    }
    loop()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.fit)
    cancelAnimationFrame(this.raf)
    clearTimeout(this.timer)
  },
  methods: {
    doorsOn(id) {
      return this.doors.filter(d => d.wall === id)
    },
    slotLeft(wall, k) {
      const slot = wall.width / this.doorsOn(wall.id).length
      return slot * k + slot / 2 - 85
    },
    state(d) {
      if (this.opened === d.name) return 'open'
      if (this.hovered === d.name) return 'hover'
      return 'closed'
    },
    isActive(d) {
      return this.state(d) !== 'closed'
    },

    leafStyle(d) {
      const angle = { closed: 0, hover: -42, open: -108 }[this.state(d)]
      return {
        transform: `rotateY(${angle}deg)`,
        boxShadow: this.isActive(d) ? '-12px 0 30px #00fe7f66, inset 0 0 30px #0006' : 'inset 0 0 30px #0006',
      }
    },
    iconStyle(d) {
      return this.isActive(d)
        ? { transform: 'scale(1.5)', boxShadow: '0 0 20px #00fe7f, 0 0 50px #00fe7f' }
        : { transform: 'scale(1)' }
    },
    peekStyle(d) {
      const st = this.state(d)
      if (st === 'open') return { opacity: 1, transform: 'scale(2.2)' }
      if (st === 'hover') return { opacity: 1, transform: 'scale(1.6) translateY(0)' }
      return { opacity: 0, transform: 'scale(.3) translateY(30px)' }
    },
    glowOpacity(d) {
      return { open: 1, hover: 0.85, closed: 0 }[this.state(d)]
    },
    icon(k, size) {
      return `<svg viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">${ICONS[k]}</svg>`
    },

    enter(door) {
      if (this.busy) return
      this.opened = door.name
      this.timer = setTimeout(() => {
        this.overlay = door
        this[door.action]()
        // Academy abre de forma síncrona y no cambia loading
        this.$nextTick(() => {
          if (this.loading) this.finish()
        })
      }, 800)
    },
    finish() {
      clearTimeout(this.timer)
      this.timer = setTimeout(this.close, 900)
    },
    close() {
      this.overlay = null
      this.opened = null
    },

    onMove(e) {
      const r = this.$refs.stage.getBoundingClientRect()
      this.cam.tx = ((e.clientX - r.left) / r.width - 0.5) * 2
      this.cam.ty = ((e.clientY - r.top) / r.height - 0.5) * 2
    },
    onLeave() {
      this.cam.tx = 0
      this.cam.ty = 0
    },
    fit() {
      const stage = this.$refs.stage
      if (!stage) return
      this.scale = Math.min(1, stage.clientWidth / 1000, stage.clientHeight / 620)
    },
    applyCamera() {
      const s = this.scale, c = this.cam
      this.$refs.world.style.transform =
        `translateZ(${250 * s}px) scale3d(${s},${s},${s}) rotateY(${c.x * 14}deg) rotateX(${-c.y * 6}deg) translateZ(350px)`
    },

    shortener() {
      this.loading = false

      var data = {
        email: this.user.email,
        name: this.user.name,
        code: 'Usbr1tM@g4Kr8SYGOtpiS2RmAf115CXAnc^c^Vkv'
      }
      var url = `https://inxaitc.co/admin/api/validate`
      axios
        .post(url,data)
        .then((response) => {
          if(response.data.status == 200) {
            var path = `https://inxaitc.co/admin/login-api?uuid=${response.data.uuid}`
            window.open(path, "_blank");
          } else {
            window.$toast.warning('Error en conexión')
          }
          this.loading = true
        })
        .catch((error) => {
          console.log(error)
          this.loading = true
        })

    },
    mailjet() {
      this.loading = false

      var data = {
        email: this.user.email,
        name: this.user.name,
        code: '^6P2GqXANB3LAL0&s7EY@d&at2VAfuTLOl%GFTwu'
      }
      var url = `https://sendingmails.inxaitcorp.com/api/validate`
      axios
        .post(url,data)
        .then((response) => {
          if(response.data.status == 200) {
            var path = `https://sendingmails.inxaitcorp.com/login-api?uuid=${response.data.uuid}`
            window.open(path, "_blank");
          } else {
            window.$toast.warning('Error en conexión')
          }
          this.loading = true
        })
        .catch((error) => {
          console.log(error)
          this.loading = true
        })

    },
    catalog() {
      this.loading = false

      var data = {
        email: this.user.email,
        name: this.user.name,
        code: 'C5cbzUbS1*QbOYd2pfF&x%uA42m6*WW1aeJ0E@6q'
      }
      var url = `https://catalogodigital.inxaitcorp.com/api/validate`
      axios
        .post(url,data)
        .then((response) => {
          if(response.data.status == 200) {
            var path = `https://catalogodigital.inxaitcorp.com/login-api?uuid=${response.data.uuid}`
            window.open(path, "_blank");
          } else {
            window.$toast.warning('Error en conexión')
          }
          this.loading = true
        })
        .catch((error) => {
          console.log(error)
          this.loading = true
        })

    },
    academy() {
      this.loading = false

      var data = {
        email: this.user.email,
        name: encodeURIComponent(this.user.name),
        avatar: this.user.avatar,
        token: 'XZ9cBaJSjyR0ZO7hWEZp879pHQqFOiqy'
      }

      var path = `https://academy.inxaitcorp.com/users/login-external/?token=${data.token}&email=${data.email}&name=${data.name}&avatar=${data.avatar}`
      //console.log(path)
      window.open(path, "_blank");
      this.loading = true
      
    },
    flows() {
      this.loading = false

      var data = {
        email: this.user.email,
        name: this.user.name,
        code: 'wwPWpc0oQG2q629223NUy1maf9JSAp'
      }
      var url = `https://flows.inxaitc.co/api/validate-user`
      axios
        .post(url,data)
        .then((response) => {
          if(response.data.status == 200) {
            var path = `https://flows.inxaitc.co/login-api?uuid=${response.data.uuid}`
            window.open(path, "_blank");
          } else {
            window.$toast.warning('Error en conexión')
          }
          this.loading = true
        })
        .catch((error) => {
          console.log(error)
          this.loading = true
        })

    },
  },
}
</script>

<style scoped>
.hub {
  --W: 900px; --H: 440px; --D: 700px;
  --brand: #0d05f2; --deep: #030ba6; --neon: #00fe7f;
  position: relative;
  height: 620px;
  max-height: calc(100vh - 180px);
  min-height: 420px;
  overflow: hidden;
  border-radius: 0.375rem;
  background: #01031f;
  color: #fff;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
  perspective: 1000px;
  perspective-origin: 50% 45%;
}

/* ---------- Geometría del cuarto ---------- */
/* El contenedor no debe capturar el mouse: si no, tapa las puertas */
.world { position: relative; width: var(--W); height: var(--H); flex-shrink: 0; transform-style: preserve-3d; pointer-events: none; }
.face  { position: absolute; transform-style: preserve-3d; pointer-events: none; }

.back  { width: var(--W); height: var(--H); transform: translateZ(calc(var(--D) * -1)); }
.floor { width: var(--W); height: var(--D); top: var(--H); transform-origin: top; transform: rotateX(-90deg); }
.ceil  { width: var(--W); height: var(--D); top: 0; transform-origin: top; transform: rotateX(-90deg); }
.left  { width: var(--D); height: var(--H); left: 0; transform-origin: left; transform: rotateY(90deg); }
.right { width: var(--D); height: var(--H); left: calc(var(--W) - var(--D)); transform-origin: right; transform: rotateY(-90deg); }

.wall-bg {
  background:
    linear-gradient(#00fe7f14 1px, transparent 1px) 0 0 / 50px 50px,
    linear-gradient(90deg, #00fe7f14 1px, transparent 1px) 0 0 / 50px 50px,
    linear-gradient(180deg, #030ba6, #0a0a5c 70%, #05053a);
}
.floor-bg {
  background:
    linear-gradient(#00fe7f33 2px, transparent 2px) 0 0 / 70px 70px,
    linear-gradient(90deg, #00fe7f33 2px, transparent 2px) 0 0 / 70px 70px,
    radial-gradient(ellipse at 50% 30%, #0d05f2 0%, #030ba6 45%, #01043d 100%);
}
.ceil-bg { background: linear-gradient(#020535, #030ba6); }

.lamp { position: absolute; left: 15%; right: 15%; height: 8px; border-radius: 9999px; }
.lamp--neon  { background: var(--neon);  box-shadow: 0 0 12px #00fe7f, 0 0 32px #00fe7f88; }
.lamp--brand { background: var(--brand); box-shadow: 0 0 18px #0d05f2, 0 0 48px #0d05f288; }

/* ---------- Puerta ---------- */
/* Cada puerta es su propia mini-escena 3D, aplanada sobre la pared */
.door {
  position: absolute;
  bottom: 0;
  width: 170px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
  pointer-events: auto;
  transform-style: flat;
}
.door:focus { outline: none; }
.door:disabled { cursor: wait; }

.sign {
  color: var(--neon);
  font-weight: 600;
  letter-spacing: 0.05em;
  font-size: 0.95rem;
  white-space: nowrap;
  border: 1px solid #00fe7f99;
  border-radius: 0.375rem;
  padding: 4px 12px;
  background: #030ba6cc;
  box-shadow: 0 0 12px #00fe7f, 0 0 32px #00fe7f88;
  transition: transform .3s, text-shadow .3s;
}
.sign--active { transform: translateY(-6px) scale(1.08); text-shadow: 0 0 6px #00fe7f, 0 0 20px #00fe7f; }

.frame {
  position: relative;
  display: block;
  width: 150px;
  height: 270px;
  border-radius: 75px 75px 0 0;
  background: linear-gradient(to bottom, var(--neon), var(--brand));
  box-shadow: 0 0 12px #00fe7f, 0 0 32px #00fe7f88;
  transform-style: flat;
  perspective: 600px;
  perspective-origin: 50% 50%;
}
.hole, .glow, .leaf { position: absolute; top: 6px; right: 6px; bottom: 6px; left: 6px; border-radius: 70px 70px 0 0; }
.hole { background: #000425; }
.glow { background: radial-gradient(circle at 50% 60%, #ffffff, #00fe7f 35%, #0d05f2 80%); transition: opacity .5s; }

.peek-wrap { position: absolute; top: 0; right: 0; bottom: 0; left: 0; display: flex; align-items: center; justify-content: center; pointer-events: none; }
.peek {
  display: flex;
  color: var(--deep);
  filter: drop-shadow(0 0 8px #ffffff);
  transition: transform .55s cubic-bezier(.34,1.56,.64,1) .08s, opacity .3s .08s;
}

.leaf {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  background: linear-gradient(to bottom right, var(--brand), var(--deep), #020766);
  border: 1px solid #ffffff1a;
  transform-origin: left center;
  backface-visibility: visible;
  transition: transform .8s cubic-bezier(.34,1.35,.5,1), box-shadow .4s;
  will-change: transform;
}
.leaf-icon {
  width: 64px;
  height: 64px;
  border-radius: 9999px;
  background: var(--deep);
  border: 2px solid #00fe7fb3;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--neon);
  box-shadow: 0 0 12px #00fe7f, 0 0 32px #00fe7f88;
  transition: transform .45s cubic-bezier(.34,1.56,.64,1), box-shadow .3s;
}
.leaf-line { width: 80px; height: 1px; background: #00fe7f66; }
.knob {
  position: absolute;
  right: 16px;
  top: 50%;
  width: 12px;
  height: 12px;
  border-radius: 9999px;
  background: var(--neon);
  box-shadow: 0 0 12px #00fe7f, 0 0 32px #00fe7f88;
}

/* ---------- Transición ---------- */
.overlay {
  position: absolute;
  top: 0; right: 0; bottom: 0; left: 0;
  z-index: 50;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  background: linear-gradient(to bottom, #030ba6f2, #01031ff2);
}
.overlay-icon {
  width: 96px;
  height: 96px;
  border-radius: 1rem;
  background: var(--brand);
  box-shadow: 0 0 18px #0d05f2, 0 0 48px #0d05f288;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--neon);
}
.overlay-title { font-size: 1.75rem; font-weight: 700; }
.overlay-text { color: #ffffffb3; font-size: 0.875rem; }
.overlay-bar { width: 192px; height: 4px; border-radius: 9999px; background: #ffffff1a; overflow: hidden; }
.overlay-bar__fill {
  width: 40%;
  height: 100%;
  background: var(--neon);
  box-shadow: 0 0 12px #00fe7f, 0 0 32px #00fe7f88;
  animation: loading-bar 1.2s ease-in-out infinite;
}

@keyframes loading-bar { 0% { transform: translateX(-100%); } 100% { transform: translateX(250%); } }
@keyframes flicker { 0%, 92%, 94%, 97%, 100% { opacity: 1; } 93% { opacity: .4; } 96% { opacity: .6; } }
.flicker { animation: flicker 4s infinite; }
.fade-enter-active, .fade-leave-active { transition: opacity .5s; }
.fade-enter, .fade-leave-to { opacity: 0; }
</style>



// WEBPACK FOOTER //
// Platforms.vue?0a4fef32